import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Refresh as RefreshIcon, ArrowUpward } from "@mui/icons-material";
import ConfirmationModal from "../ConfirmationModal";
import Star from "../../assets/svgs/exact-report-logo.svg";
import { AvatarLogOut } from "../AvatarLogOut";
import tokens from "../../styles/designTokens";
import { body1 } from "../../styles/typography";
import { useLocation } from "react-router";
import {
  selectActiveJurisdiction,
  selectJurisdictions
} from "../../selectors/suggestions";
import { fetchJurisdictions, Jurisdiction, setActiveJurisdiction } from '../../redux/suggestions';
import WarningRafiki1Img from "../../assets/svgs/warning-rafiki-1.svg";
import JurisdictionDropDown from "../JurisdictionDropDown";
import { AppDispatch } from "../../store";
import useFeatureFlags from "../../hooks/useFeatureFlags";

const useStyles = makeStyles(() => ({
  stickyHeader: {
    position: 'sticky',
    top: 0,
    zIndex: 1000,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '8px',
    padding: '25px 20px 5px 25px',
    backgroundColor: tokens.white
  },
  subtitle: {
    ...body1,
    color: tokens.core3,
    marginRight: 'auto'
  },
  iconButtons: {
    width: '36px',
    height: '36px',
    borderRadius: '72px',
    backgroundColor: 'transparent',
    color: tokens.core2,
    transition: 'background-color 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: tokens.neutral80,
      color: tokens.core2
    },
    '& .MuiSvgIcon-root': {
      fontSize: '16px'
    }
  }
}));

interface ExactReportHeaderProps {
  refreshAnalysis: (options: { jurisdictionId?: string }) => void;
  scrollableContainerRef: React.RefObject<HTMLDivElement>;
}

const ExactReportHeader: React.FC<ExactReportHeaderProps> = ({ refreshAnalysis, scrollableContainerRef }) => {
  const classes = useStyles();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { getFlag } = useFeatureFlags();
  const [clearCommentsModalOpen, setClearCommentsModalOpen] = useState<boolean>(false);
  const [showJumpToTop, setShowJumpToTop] = useState<boolean>(false);
  const scrollCountRef = useRef<number>(0);
  const activeJurisdictionId = useSelector(selectActiveJurisdiction);
  const jurisdictions = useSelector(selectJurisdictions);
  const activeJurisdiction = jurisdictions.find(j => j.id === activeJurisdictionId) || null;
  const [selectedJurisdiction, setSelectedJurisdiction] = useState<Jurisdiction | null>(activeJurisdiction);
  const showHeaderAndRefreshButton = location.pathname === '/results';
  const projectJurisdictionSelectorCP1 = getFlag('projectJurisdictionSelectorCp1');

  useEffect(() => {
    if (jurisdictions.length === 0) {
      dispatch(fetchJurisdictions());
    }
  }, [jurisdictions, dispatch]);

  useEffect(() => {
    const container = scrollableContainerRef.current;
    if (!container) return () => { };

    const scrollThreshold = 300; // pixels from top

    const handleScroll = () => {
      const currentScrollY = container.scrollTop;
      setShowJumpToTop(currentScrollY > scrollThreshold);
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [scrollableContainerRef]);

  const toggleRefreshAnalysisModal = () => {
    setSelectedJurisdiction(activeJurisdiction);
    setClearCommentsModalOpen(!clearCommentsModalOpen);
  };

  const handleJumpToTop = () => {
    if (scrollableContainerRef.current) {
      scrollableContainerRef.current.scrollTo({ top: 0, behavior: "smooth" });
      scrollCountRef.current = 0;
      setShowJumpToTop(false);
    }
  };

  const closeModalAndRefreshAnalysis = () => {
    dispatch(setActiveJurisdiction(selectedJurisdiction ? selectedJurisdiction.id : null));
    toggleRefreshAnalysisModal();
    refreshAnalysis({ jurisdictionId: selectedJurisdiction ? selectedJurisdiction.id : null });
  };

  return (
    <Box className={classes.stickyHeader}>
      <Star />
      {showHeaderAndRefreshButton && (
        <>
          <Typography className={classes.subtitle}>AI Assistant</Typography>
          {showJumpToTop && (
            <Tooltip title="Back to the top">
              <IconButton className={classes.iconButtons} aria-label="Jump to the top" onClick={handleJumpToTop}>
                <ArrowUpward />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Refresh analysis">
            <IconButton
              className={classes.iconButtons}
              aria-label="Refresh analysis"
              onClick={toggleRefreshAnalysisModal}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <ConfirmationModal
            title='Refresh analysis'
            isOpen={clearCommentsModalOpen}
            onClose={toggleRefreshAnalysisModal}
            buttonAction={closeModalAndRefreshAnalysis}
            cancelAction={toggleRefreshAnalysisModal}
            disabled={projectJurisdictionSelectorCP1 && !selectedJurisdiction}
          >
            <div style={{ textAlign: 'center' }}>
              <WarningRafiki1Img />
            </div>

            <div style={{ marginTop: tokens.spacing4xs }}>
              <Typography>Refreshing the analysis cannot be undone. Any existing recommendations will be removed and new recommendations will be added.</Typography>
            </div>

            {projectJurisdictionSelectorCP1 && (
              <>
                <div style={{ marginTop: tokens.spacings }}>
                  <Typography>Do you want to keep the same jurisdiction?</Typography>
                </div>

                <div style={{ marginTop: tokens.spacingxs }}>
                  <JurisdictionDropDown
                    value={selectedJurisdiction}
                    onChange={(_, value) => setSelectedJurisdiction(value)}
                    label="Jurisdiction"
                  ></JurisdictionDropDown>
                </div>
              </>
            )}
          </ConfirmationModal>
        </>
      )}
      <AvatarLogOut disabled={clearCommentsModalOpen} />
    </Box>
  );
};

export default ExactReportHeader;
